import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useFormikContext, getIn } from 'formik';

interface InputMoneyProps {
  name: string;
  label: string;
  required?: boolean;
  className?: string;
  placeholder?: string;
  readonly?: boolean;
  addonBefore?: string;
  value?: number; // Novo prop para aceitar valores externos
  onValueChange?: (value: number) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void; // ✅ Adicione esta linha
}

const DECIMAL_SIZE = 2;

const InputMoney: React.FC<InputMoneyProps> = ({
  name,
  label,
  required = false,
  className = '',
  placeholder = '',
  readonly = false,
  addonBefore = 'R$',
  value: externalValue,
  onValueChange,
  onChange
}) => {
  const { setFieldValue, values, errors, touched } = useFormikContext<InputMoneyProps>();
  const value = getIn(values, name) || externalValue; // Usa o valor do Formik ou o valor externo
  const error = getIn(errors, name);
  const touch = getIn(touched, name);
  const inputRef = useRef<HTMLInputElement>(null);

  // Função para formatar o valor como moeda brasileira
  const formatCurrency = useCallback((value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'decimal',
      minimumFractionDigits: DECIMAL_SIZE,
      maximumFractionDigits: DECIMAL_SIZE,
    }).format(value);
  }, []);

  // Função para converter o valor formatado de volta para número
  const parseCurrency = useCallback((value: string): number => {
    const cleanedValue = value.replace(/[^0-9]/g, '');
    return parseFloat(cleanedValue) / 100;
  }, []);

  // Estado para o valor formatado
  const [currentValue, setCurrentValue] = useState<string>(() => formatCurrency(value || 0));

  // Atualiza o valor formatado quando o valor do Formik ou o valor externo muda
  useEffect(() => {
    setCurrentValue(formatCurrency(value || 0));
  }, [value, formatCurrency]);

  // Manipula a mudança no input
  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    // Remove caracteres não numéricos e formata o valor
    const numericValue = parseCurrency(inputValue);
    const formattedValue = formatCurrency(numericValue);

    // Atualiza o estado local e o valor no Formik
    setCurrentValue(formattedValue);
    setFieldValue(name, numericValue);
    // Adicione este trecho para notificar o componente pai
    if (onValueChange) {
      onValueChange(numericValue);
    }
    if (onChange) {
      onChange(event);
    }
  };


  return (
    <div className={`${className}`}>
      <label htmlFor={name} className="block text-sm font-medium text-gray-600 mr-2">
        {label} {required ? <b className="text-redPrimary">*</b> : ''}
      </label>
      <div className="flex items-center w-full">
        {/* Input de prefixo "R$" que é readonly */}
        <input
          value={addonBefore}
          readOnly
          className="border border-gray h-[43px] text-gray2 p-2 bg-white rounded-l w-[60px] bg-gray-100 text-center"
        />
        {/* Input para digitação do valor */}
        <input
          value={currentValue}
          onChange={handleOnChange}
          id={name}
          name={name}
          type="text"
          className={`border-l-0 border border-gray h-[43px] text-gray2 p-2 rounded-r w-full bg-[#ffffff] ${readonly ? 'bg-gray-100' : ''}`}
          placeholder={placeholder}
          readOnly={readonly}
          ref={inputRef}
          aria-label={label}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
        />
      </div>
      {touch && error && (
        <small id={`${name}-error`} className="text-redPrimary/90" aria-live="polite">
          {error}
        </small>
      )}
    </div>
  );
};

export default InputMoney;